import { createEvent, createStore } from 'effector';
import type { CameraConfig } from '../cameraConfig';
import { defaultCameraConfig } from '../cameraConfig';
import type { CollisionConfig } from '../scene/collision/colliderTypes';
import { defaultCollisionConfig } from '../scene/collision/colliderTypes';
import type { ColliderDef } from '../scene/collision/colliderTypes';
import { KAABA_DEFAULT_COLLIDERS } from '../scene/collision/kaabaColliders';
import type {
  DevConfig,
  SparkRenderSettingsConfig,
  ResponsiveRenderSettingsConfig,
  TieredRenderSettingsConfig,
} from '../devConfig';
import {
  defaultDevConfig,
  defaultResponsiveRenderSettings,
} from '../devConfig';
import type { PoiConfig } from '../poiConfig';
import { defaultPoiConfig } from '../poiConfig';
import type { TellerAssistantConfig } from '../tellerAssistantConfig';
import { defaultTellerAssistantConfig } from '../tellerAssistantConfig';
import { DEFAULT_MUSIC_TRACK_ID } from '../audio/musicTracks';

const APP_CONFIG_STORAGE_KEY = 'pilgrim.appConfig';

export interface AppConfig {
  camera: CameraConfig;
  collision: CollisionConfig;
  colliders: ColliderDef[];
  dev: DevConfig;
  poi: PoiConfig;
  audio: AudioConfig;
  tellerAssistant: TellerAssistantConfig;
}

export interface AudioConfig {
  soundOn: boolean;
  musicOn: boolean;
  musicTrackId: string;
  musicVolume: number;
}

const defaultAudioConfig: AudioConfig = {
  soundOn: false,
  musicOn: false,
  musicTrackId: DEFAULT_MUSIC_TRACK_ID,
  musicVolume: 1,
};

const cloneTieredRenderSettings = (
  tiered: TieredRenderSettingsConfig
): TieredRenderSettingsConfig => ({
  low: { ...tiered.low },
  middle: { ...tiered.middle },
  high: { ...tiered.high },
});

const cloneResponsiveRenderSettings = (
  settings: ResponsiveRenderSettingsConfig
): ResponsiveRenderSettingsConfig => ({
  desktop: cloneTieredRenderSettings(settings.desktop),
  mobile: cloneTieredRenderSettings(settings.mobile),
});

export const createDefaultAppConfig = (): AppConfig => ({
  camera: { ...defaultCameraConfig },
  collision: { ...defaultCollisionConfig },
  colliders: structuredClone(KAABA_DEFAULT_COLLIDERS),
  dev: {
    ...defaultDevConfig,
    renderSettings: cloneResponsiveRenderSettings(
      defaultResponsiveRenderSettings
    ),
  },
  poi: { ...defaultPoiConfig },
  audio: { ...defaultAudioConfig },
  tellerAssistant: { ...defaultTellerAssistantConfig },
});

export const clampVolume = (value: number) =>
  Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 1;

export const normalizeAudioConfig = (audio: AudioConfig): AudioConfig => {
  const next = {
    ...audio,
    musicVolume: clampVolume(audio.musicVolume),
  };

  if (next.musicVolume <= 0) {
    next.musicOn = false;
  }

  if (!next.musicOn) {
    next.soundOn = false;
  }

  return next;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const mergeSparkRenderSettings = (
  state: SparkRenderSettingsConfig,
  patch: Partial<SparkRenderSettingsConfig> | undefined
): SparkRenderSettingsConfig => ({
  ...state,
  ...(patch ?? {}),
});

const mergeTieredRenderSettings = (
  state: TieredRenderSettingsConfig,
  patch: Partial<TieredRenderSettingsConfig> | undefined
): TieredRenderSettingsConfig => {
  if (!isRecord(patch)) {
    return cloneTieredRenderSettings(state);
  }

  return {
    low: mergeSparkRenderSettings(
      state.low,
      isRecord(patch.low) ? (patch.low as Partial<SparkRenderSettingsConfig>) : undefined
    ),
    middle: mergeSparkRenderSettings(
      state.middle,
      isRecord(patch.middle) ? (patch.middle as Partial<SparkRenderSettingsConfig>) : undefined
    ),
    high: mergeSparkRenderSettings(
      state.high,
      isRecord(patch.high) ? (patch.high as Partial<SparkRenderSettingsConfig>) : undefined
    ),
  };
};

const mergeResponsiveRenderSettings = (
  state: ResponsiveRenderSettingsConfig,
  patch: Partial<ResponsiveRenderSettingsConfig> | undefined
): ResponsiveRenderSettingsConfig => {
  if (!isRecord(patch)) {
    return cloneResponsiveRenderSettings(state);
  }

  return {
    desktop: mergeTieredRenderSettings(
      state.desktop,
      isRecord(patch.desktop) ? (patch.desktop as Partial<TieredRenderSettingsConfig>) : undefined
    ),
    mobile: mergeTieredRenderSettings(
      state.mobile,
      isRecord(patch.mobile) ? (patch.mobile as Partial<TieredRenderSettingsConfig>) : undefined
    ),
  };
};

const mergeDevConfig = (state: DevConfig, patch: Partial<DevConfig>): DevConfig => ({
  ...state,
  ...patch,
  renderSettings: mergeResponsiveRenderSettings(
    state.renderSettings,
    isRecord(patch.renderSettings)
      ? (patch.renderSettings as Partial<ResponsiveRenderSettingsConfig>)
      : undefined
  ),
});

// JSON.stringify trashes non-finite numbers: Infinity / -Infinity / NaN
// → all serialized to `"null"`, then JSON.parse returns `null`. For
// `defaultCameraConfig.orbitMinAzimuthAngle = -Infinity` etc. this means
// every reload turned wide-open angle limits into `null` (treated as 0
// by Three.js OrbitControls), causing a one-frame camera snap on
// `restoreOrbitLocks()`. We round-trip via sentinel strings.
const INFINITY_SENTINEL = '__Infinity__';
const NEG_INFINITY_SENTINEL = '__-Infinity__';
const NAN_SENTINEL = '__NaN__';

export const jsonReplacer = (_key: string, value: unknown): unknown => {
  if (typeof value === 'number') {
    if (value === Infinity) return INFINITY_SENTINEL;
    if (value === -Infinity) return NEG_INFINITY_SENTINEL;
    if (Number.isNaN(value)) return NAN_SENTINEL;
  }
  return value;
};

export const jsonReviver = (_key: string, value: unknown): unknown => {
  if (value === INFINITY_SENTINEL) return Infinity;
  if (value === NEG_INFINITY_SENTINEL) return -Infinity;
  if (value === NAN_SENTINEL) return NaN;
  return value;
};

const readStoredAppConfig = (): Partial<AppConfig> | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(APP_CONFIG_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw, jsonReviver);
    return isRecord(parsed) ? (parsed as Partial<AppConfig>) : null;
  } catch {
    return null;
  }
};

const persistAppConfig = (state: AppConfig): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(APP_CONFIG_STORAGE_KEY, JSON.stringify(state, jsonReplacer));
  } catch {
    // Ignore storage errors to avoid breaking the app in private mode.
  }
};

export const mergeAppConfig = (
  state: AppConfig,
  patch: Partial<AppConfig>
): AppConfig => {
  // Strip null fields from camera patch — they almost certainly come from
  // a pre-fix localStorage where Infinity got JSON-stringified to "null".
  // Letting null override the (Infinity-bearing) default would lock
  // azimuth/distance/y-clamps to 0 and snap the camera. New writes use
  // the sentinel-aware replacer above, so this is a one-time cleanup.
  const cameraPatchSanitized = isRecord(patch.camera)
    ? Object.fromEntries(
        Object.entries(patch.camera).filter(([, v]) => v !== null)
      ) as Partial<CameraConfig>
    : null;
  const nextCamera = cameraPatchSanitized
    ? { ...state.camera, ...cameraPatchSanitized }
    : state.camera;
  const nextCollision = isRecord(patch.collision)
    ? { ...state.collision, ...(patch.collision as Partial<CollisionConfig>) }
    : state.collision;
  const nextColliders = Array.isArray(patch.colliders)
    ? (patch.colliders as ColliderDef[])
    : state.colliders;
  const nextDev = isRecord(patch.dev)
    ? mergeDevConfig(state.dev, patch.dev as Partial<DevConfig>)
    : state.dev;
  const nextPoi = isRecord(patch.poi)
    ? { ...state.poi, ...(patch.poi as Partial<PoiConfig>) }
    : state.poi;
  const nextAudio = isRecord(patch.audio)
    ? normalizeAudioConfig({
        ...state.audio,
        ...(patch.audio as Partial<AudioConfig>),
      })
    : state.audio;
  const patchTellerAssistant = patch.tellerAssistant as Partial<TellerAssistantConfig> | undefined;
  const patchTransform = patchTellerAssistant?.transform;
  const nextTellerAssistant = isRecord(patch.tellerAssistant)
    ? {
        ...state.tellerAssistant,
        ...(patchTellerAssistant),
        transform: {
          ...defaultTellerAssistantConfig.transform,
          ...state.tellerAssistant.transform,
          ...(isRecord(patchTransform) ? patchTransform : {}),
          desktop: {
            ...defaultTellerAssistantConfig.transform.desktop,
            ...state.tellerAssistant.transform?.desktop,
            ...(isRecord(patchTransform?.desktop) ? patchTransform?.desktop : {}),
          },
          mobile: {
            ...defaultTellerAssistantConfig.transform.mobile,
            ...state.tellerAssistant.transform?.mobile,
            ...(isRecord(patchTransform?.mobile) ? patchTransform?.mobile : {}),
          },
        },
        lighting: isRecord(patchTellerAssistant?.lighting)
          ? {
              ...state.tellerAssistant.lighting,
              ...(patchTellerAssistant?.lighting as Partial<TellerAssistantConfig['lighting']>),
              simple: {
                ...defaultTellerAssistantConfig.lighting.simple,
                ...state.tellerAssistant.lighting.simple,
                ...(isRecord(patchTellerAssistant?.lighting?.simple)
                  ? (patchTellerAssistant?.lighting?.simple as Partial<TellerAssistantConfig['lighting']['simple']>)
                  : {}),
              },
              env: {
                ...defaultTellerAssistantConfig.lighting.env,
                ...state.tellerAssistant.lighting.env,
                ...(isRecord(patchTellerAssistant?.lighting?.env)
                  ? (patchTellerAssistant?.lighting?.env as Partial<TellerAssistantConfig['lighting']['env']>)
                  : {}),
              },
            }
          : state.tellerAssistant.lighting,
      }
    : state.tellerAssistant;

  return {
    camera: nextCamera,
    collision: nextCollision,
    colliders: nextColliders,
    dev: nextDev,
    poi: nextPoi,
    audio: nextAudio,
    tellerAssistant: nextTellerAssistant,
  };
};

export const updateAppConfig = createEvent<Partial<AppConfig>>();
export const updateCameraConfig = createEvent<Partial<CameraConfig>>();
export const updateCollisionConfig = createEvent<Partial<CollisionConfig>>();
export const updateColliders = createEvent<ColliderDef[]>();
export const updateSingleCollider = createEvent<{ id: string; patch: Partial<ColliderDef> }>();
export const updateDevConfig = createEvent<Partial<DevConfig>>();
export const updatePoiConfig = createEvent<Partial<PoiConfig>>();
export const updateAudioConfig = createEvent<Partial<AudioConfig>>();
export const updateTellerAssistantConfig = createEvent<Partial<TellerAssistantConfig>>();
export const resetAppConfig = createEvent();

const initialConfig = (() => {
  const defaults = createDefaultAppConfig();
  const stored = readStoredAppConfig();
  return stored ? mergeAppConfig(defaults, stored) : defaults;
})();

export const $appConfig = createStore<AppConfig>(initialConfig)
  .on(updateAppConfig, (state, patch) => mergeAppConfig(state, patch))
  .on(updateCameraConfig, (state, patch) => ({
    ...state,
    camera: { ...state.camera, ...patch },
  }))
  .on(updateCollisionConfig, (state, patch) => ({
    ...state,
    collision: { ...state.collision, ...patch },
  }))
  .on(updateColliders, (state, colliders) => ({
    ...state,
    colliders,
  }))
  .on(updateSingleCollider, (state, { id, patch }) => ({
    ...state,
    colliders: state.colliders.map((c) =>
      c.id === id ? ({ ...c, ...patch } as ColliderDef) : c
    ),
  }))
  .on(updateDevConfig, (state, patch) => ({
    ...state,
    dev: { ...state.dev, ...patch },
  }))
  .on(updatePoiConfig, (state, patch) => ({
    ...state,
    poi: { ...state.poi, ...patch },
  }))
  .on(updateAudioConfig, (state, patch) => {
    const nextAudio = normalizeAudioConfig({ ...state.audio, ...patch });

    return {
      ...state,
      audio: nextAudio,
    };
  })
  .on(updateTellerAssistantConfig, (state, patch) => ({
    ...state,
    tellerAssistant: { ...state.tellerAssistant, ...patch },
  }))
  .on(resetAppConfig, () => createDefaultAppConfig());

$appConfig.watch((state) => {
  persistAppConfig(state);
});

export const $cameraConfig = $appConfig.map((config) => config.camera);
export const $collisionConfig = $appConfig.map((config) => config.collision);
export const $collidersConfig = $appConfig.map((config) => config.colliders);
export const $devConfig = $appConfig.map((config) => config.dev);
export const $poiConfig = $appConfig.map((config) => config.poi);
export const $audioConfig = $appConfig.map((config) => config.audio);

// Preview Model waves configuration (not persisted to localStorage)
export interface PreviewModelConfig {
  wavesCenter: { x: number; y: number; z: number };
  wavesMinDistance: number;    // 0-1 - minimum distance from center (as fraction of model size)
  wavesAmplitude: number;      // 1-10 - wave width
  wavesPeriod: number;         // 1-10 - number of simultaneous waves
  wavesSpeed: number;          // speed multiplier
  wavesColorIntensity: number; // 0-10 - color intensity
  wavesDisplacement: number;   // 0-0.1 - displacement as percentage
}

export const defaultPreviewModelConfig: PreviewModelConfig = {
  wavesCenter: { x: 0, y: 0, z: 0 },
  wavesMinDistance: 0,
  wavesAmplitude: 3.0,
  wavesPeriod: 1.0,
  wavesSpeed: 1.0,
  wavesColorIntensity: 5.0,
  wavesDisplacement: 0.01,
};

export const updatePreviewModelConfig = createEvent<Partial<PreviewModelConfig>>();
export const resetPreviewModelConfig = createEvent();

export const $previewModelConfig = createStore<PreviewModelConfig>(defaultPreviewModelConfig)
  .on(updatePreviewModelConfig, (state, patch) => ({
    ...state,
    ...patch,
    wavesCenter: patch.wavesCenter
      ? { ...state.wavesCenter, ...patch.wavesCenter }
      : state.wavesCenter,
  }))
  .on(resetPreviewModelConfig, () => defaultPreviewModelConfig);

// Background configuration (not persisted to localStorage)
export interface BackgroundConfigState {
  color: number;
  skyboxEnabled: boolean;
  skyboxUrl: string;            // absolute URL for skybox texture (empty = use scene default)
  skyboxIntensity: number;      // 0-1
  skyboxBlur: number;           // 0-1
  skyboxRotation: [number, number, number]; // degrees
}

export const defaultBackgroundConfig: BackgroundConfigState = {
  color: 0x0f0f13,
  skyboxEnabled: true,
  skyboxUrl: '',
  skyboxIntensity: 1.0,
  skyboxBlur: 0.0,
  skyboxRotation: [0, 0, 0],
};

export const updateBackgroundConfig = createEvent<Partial<BackgroundConfigState>>();

export const $backgroundConfig = createStore<BackgroundConfigState>(defaultBackgroundConfig)
  .on(updateBackgroundConfig, (state, patch) => ({
    ...state,
    ...patch,
    skyboxRotation: patch.skyboxRotation ?? state.skyboxRotation,
  }));

export const $tellerAssistantConfig = $appConfig.map((config) => config.tellerAssistant);

// Gizmo target state (not persisted)
export const setGizmoTarget = createEvent<string | null>();
export const $gizmoTargetId = createStore<string | null>(null)
  .on(setGizmoTarget, (_, id) => id);
